// @flow
import invariant from 'invariant';
import { getJson } from 'utils/http';
import algoliaSearch from 'utils/algoliaSearch';
import { trackEvent } from 'utils/analytics';
import getMainSearchTrackingData from 'utils/getMainSearchTrackingData';
import getSearchQueryOptions from 'utils/search/getSearchQueryOptions';
import getSearchIndex from 'utils/search/getSearchIndex';
import { getDefaultSearchRadius } from 'utils/constants';
import { configString } from 'utils/config';
import { isEnabled as daysEnabled } from 'utils/idealistdayevents';
import cloneDeep from 'lodash.clonedeep';
import { setSearchRadius as setSearchRadiusBase } from './searchRadius';
import type { State, Dispatch, GetState } from '..';
import type {
  SortType,
  GradSortType,
  MainSearchResults
} from '../reducers/mainSearch';
import { updateSearchLocation, clearSearchLocation } from './currentUser';

type InitializeAction = {
  type: 'SEARCH_INITIALIZE',
  queryParams: Object,
  defaultSearchType?: string
};
type InitializeLandingPageAction = {
  type: 'SEARCH_INITIALIZE_LANDING_PAGE',
  landingPagePath: ?string,
  filters: ?Object,
  autosaveSearch?: boolean
};
type ResetPageAction = { type: 'SEARCH_RESET_PAGE' };
type SetQueryAction = {
  type: 'SEARCH_SET_QUERY',
  query: string
};
type SetQueryKeywordsAction = {
  type: 'SET_QUERY_KEYWORDS',
  keywords: Array<string>
};
type SetSortAction = {
  type: 'SEARCH_SET_SORT',
  sortParam: SortType | GradSortType
};
type SetPageIndexAction = { type: 'SEARCH_SET_PAGE_INDEX', pageIndex: number };
type ResetFiltersAction = {
  type: 'SEARCH_RESET_FILTERS'
};
type SetFilterAction = {
  type: 'SEARCH_SET_FILTER',
  key: string,
  value: ?string
};
type SelectFilterOptionAction = {
  type: 'SEARCH_SELECT_FILTER_OPTION',
  key: string,
  option: ?string
};
type DeselectFilterOptionAction = {
  type: 'SEARCH_DESELECT_FILTER_OPTION',
  key: string,
  option: ?string
};
type SelectFilterSpecificOptionsAction = {
  type: 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS',
  key: string,
  options: Array<string>
};
type SelectAllFilterOptionsAction = {
  type: 'SEARCH_SELECT_ALL_FILTER_OPTIONS',
  key: string
};
type SetSearchFacetOptionsAction = {
  type: 'SET_SEARCH_FACET_OPTIONS',
  options: Array<string>,
  optionTitles: Array<string>,
  facetKey: string,
  optionRankings: Object
};
type ClearResultsAction = {
  type: 'SEARCH_CLEAR_RESULTS'
};
type SearchInitiatedByUserAction = {
  type: 'SEARCH_INITIATED_BY_USER',
  searchMode?: boolean
};
type SearchStartedAction = { type: 'SEARCH_STARTED' };
type SuccessAction = {
  type: 'SEARCH_RESULTS_SUCCESS',
  results: MainSearchResults,
  hasOrgResults?: boolean
};
type FetchPageSuccessAction = {
  type: 'SEARCH_FETCH_PAGE_SUCCESS',
  results: MainSearchResults
};
type SearchAutosaveDisableAction = {
  type: 'SEARCH_AUTOSAVE_DISABLE'
};
type UninitializeSearchAction = {
  type: 'SEARCH_UNINITIALIZE'
};

export type Action =
  | InitializeAction
  | InitializeLandingPageAction
  | ResetPageAction
  | SetQueryAction
  | SetQueryKeywordsAction
  | SetSortAction
  | SetPageIndexAction
  | ResetFiltersAction
  | SetFilterAction
  | SelectFilterOptionAction
  | DeselectFilterOptionAction
  | SelectFilterSpecificOptionsAction
  | SelectAllFilterOptionsAction
  | SetSearchFacetOptionsAction
  | ClearResultsAction
  | SearchInitiatedByUserAction
  | SearchStartedAction
  | SuccessAction
  | FetchPageSuccessAction
  | SearchAutosaveDisableAction
  | UninitializeSearchAction;

const searchResultsSuccessful = ({
  results,
  hasOrgResults
}: {
  results: MainSearchResults,
  hasOrgResults?: boolean
}): SuccessAction => ({
  type: 'SEARCH_RESULTS_SUCCESS',
  results,
  hasOrgResults
});

const fetchPageSuccessful = ({
  results
}: {
  results: MainSearchResults
}): FetchPageSuccessAction => ({
  type: 'SEARCH_FETCH_PAGE_SUCCESS',
  results
});

const fetchOrgResultsCount = ({
  state: {
    mainSearch: { query }
  }
}: {
  state: State
}): Promise<*> => {
  const searchConfig = {
    appId: configString('algolia', 'appId'),
    apiKey: configString('algolia', 'searchApiKey')
  };

  const queryOptions = {
    index: getSearchIndex(),
    facets: 'type',
    hitsPerPage: 0,
    filters: "type:'ORG'"
  };

  return algoliaSearch(searchConfig, query, queryOptions, false).then(
    results => (results.facets.type && results.facets.type.ORG) || 0
  );
};

const executeSearchQuery = ({
  state: {
    mainSearch,
    currentUser,
    searchRadius: { value: searchRadius }
  },
  pageIndex,
  analytics
}: {
  state: State,
  pageIndex?: number,
  analytics: boolean
}): Promise<*> => {
  const timestamp = new Date();
  const sort = mainSearch.sort[mainSearch.filters.type];
  // Include additional filter options to go with certain options.
  const filters = cloneDeep(mainSearch.filters);
  Object.keys(filters).forEach(filterKey => {
    const facet = mainSearch.searchFacets[filterKey];
    const filter = filters[filterKey];
    if (facet && facet.includeAdditionalOption) {
      const updatedFilter = [...filter];
      filter.forEach(filterOption => {
        if (filterOption in facet.includeAdditionalOption) {
          updatedFilter.push(facet.includeAdditionalOption[filterOption]);
        }
      });
      filters[filterKey] = updatedFilter;
    }
  });
  const queryOptions = getSearchQueryOptions(mainSearch.searchFacets, {
    location: currentUser.searchLocation,
    radius: searchRadius,
    ...mainSearch,
    filters,
    sort,
    pageIndex: pageIndex !== undefined ? pageIndex : mainSearch.pageIndex,
    analytics
  });

  const searchConfig = {
    appId: configString('algolia', 'appId'),
    apiKey: configString('algolia', 'searchApiKey')
  };

  if (!daysEnabled()) {
    if (queryOptions.filters === undefined) {
      queryOptions.filters = '';
    }
    if (queryOptions.filters) {
      queryOptions.filters += ' AND ';
    }
    queryOptions.filters += 'NOT type:"IDEALIST_DAY_EVENT"';
  }

  return algoliaSearch(searchConfig, mainSearch.query, queryOptions).then(
    res => ({
      // Add a timestamp to keep track of when the query was executed
      ...res,
      // Add the searchLocation so we know which location the results are for
      searchLocation: currentUser.searchLocation,
      searchId: mainSearch.searchId,
      timestamp,
      // This is for analytics
      searchParameters: {
        query: mainSearch.query,
        queryOptions
      }
    })
  );
};

const loadDynamicFacetOptions = () => (
  dispatch: Dispatch,
  getState: GetState
) => {
  const { facetsThatArentLoaded } = getState().mainSearch;
  return Promise.all(
    [
      {
        facetUrlSegment: 'idealist-day-dates',
        facetKey: 'idealistDayFacet',
        hasLang: false,
        responseToOptions: ({ dates }) => dates
      }
    ].map(({ facetUrlSegment, facetKey, hasLang, responseToOptions }) => {
      if (!facetsThatArentLoaded.includes(facetKey)) {
        return Promise.resolve();
      }
      return getJson(
        `/data/website/${facetUrlSegment}${
          hasLang ? `?lang=${USER_LOCALE}` : ''
        }`
      ).then(response => {
        const options = responseToOptions(response);
        dispatch({
          type: 'SET_SEARCH_FACET_OPTIONS',
          facetKey,
          options: options.map(f => f.id),
          optionTitles: options.reduce(
            (sum, { id, text }) => ({
              ...sum,
              [id]: text
            }),
            {}
          ),
          optionRankings: options.reduce(
            (sum, { id, usageRanking }) => ({
              ...sum,
              [id]: usageRanking
            }),
            {}
          )
        });
      });
    })
  );
};

export const initializeSearch = (action: InitializeAction = {}) => (
  dispatch: Dispatch
) => {
  Promise.all([
    dispatch({
      type: 'SEARCH_INITIALIZE',
      ...action
    }),
    dispatch(loadDynamicFacetOptions())
  ]);
};

export const uninitializeSearch = () => (dispatch: Dispatch) =>
  dispatch({ type: 'SEARCH_UNINITIALIZE' });

export const initializeLandingPageSearch = ({
  location,
  searchRadius,
  landingPagePath,
  filters,
  autosaveSearch
}: {
  location?: SearchLocation,
  searchRadius?: string,
  landingPagePath?: string,
  filters?: SearchFilters,
  autosaveSearch?: boolean
} = {}) => (dispatch: Dispatch) => {
  const initializeSearchAction = {
    type: 'SEARCH_INITIALIZE_LANDING_PAGE',
    landingPagePath,
    filters,
    autosaveSearch
  };

  if (searchRadius) {
    dispatch(setSearchRadiusBase(searchRadius, false /* userHasChanged */));
  }
  if (location) {
    return Promise.all([
      dispatch(initializeSearchAction),
      dispatch(loadDynamicFacetOptions()),
      dispatch(updateSearchLocation(location, false))
    ]);
  }
  return Promise.all([
    dispatch(initializeSearchAction),
    dispatch(loadDynamicFacetOptions())
  ]);
};

export const searchResetPage = (): ResetPageAction => ({
  type: 'SEARCH_RESET_PAGE'
});

export const setSearchQuery = (query: string, didUserChange: boolean) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  if (didUserChange) {
    trackEvent('Changed Search Query', {
      new_query: query,
      ...getMainSearchTrackingData(getState())
    });
  }
  dispatch({ type: 'SEARCH_SET_QUERY', query });
};

export const setQueryKeywords = (keywords: Array<string>) => (
  dispatch: Dispatch
) => dispatch({ type: 'SET_QUERY_KEYWORDS', keywords });

export const setSearchRadius = (radius: string, didUserChange: boolean) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  if (didUserChange) {
    trackEvent('Changed Search Radius', {
      radius,
      ...getMainSearchTrackingData(getState())
    });
  }
  dispatch(setSearchRadiusBase(radius, didUserChange));
  dispatch(searchResetPage());
};

export const setSearchSort = (sortParam: SortType | GradSortType) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  const state = getState();
  const searchRadius = state.searchRadius;
  trackEvent('Changed Search Sort', {
    sort_order: sortParam,
    ...getMainSearchTrackingData(state)
  });
  dispatch({ type: 'SEARCH_SET_SORT', sortParam });
  if (!searchRadius.userHasChanged) {
    dispatch(setSearchRadius(getDefaultSearchRadius(sortParam), false));
  }
  dispatch(setQueryKeywords([]));
};

export const setPageIndex = (pageIndex: number, didUserChange: boolean) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  if (didUserChange) {
    trackEvent('Changed Search Page Number', {
      new_page: pageIndex,
      ...getMainSearchTrackingData(getState())
    });
  }
  dispatch({ type: 'SEARCH_SET_PAGE_INDEX', pageIndex });
  dispatch(setQueryKeywords([]));
};

export const resetSearchFilters = (): ResetFiltersAction => ({
  type: 'SEARCH_RESET_FILTERS'
});

export const clearAllFilters = () => (
  dispatch: Dispatch,
  getState: GetState
) => {
  const type = getState().mainSearch.filters.type;
  const sort = getState().mainSearch.sort[type];
  dispatch(resetSearchFilters());
  dispatch(setSearchRadius(getDefaultSearchRadius(sort), false));
  dispatch(clearSearchLocation(true));
  dispatch(setQueryKeywords([]));
};

export const setSearchFilter = (filterKey: string, value: ?string) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  if (filterKey === 'type') {
    invariant(value, 'type can never be set to undefined/null');
  }
  if (value === 'BLOG' && filterKey === 'type') return;
  trackEvent('Changed Search Filter', {
    filter_name: filterKey,
    filter_value: value,
    ...getMainSearchTrackingData(getState())
  });
  dispatch({ type: 'SEARCH_SET_FILTER', key: filterKey, value });
  dispatch(setQueryKeywords([]));
};

export const selectSearchFilterOption = (
  filterKey: string,
  option: ?string
) => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  const selectedOptions = [
    ...(state.mainSearch.filters[filterKey] || []),
    option
  ];
  trackEvent('Changed Search Filter', {
    filter_name: filterKey,
    filter_value: selectedOptions,
    ...getMainSearchTrackingData(state)
  });
  dispatch({ type: 'SEARCH_SELECT_FILTER_OPTION', key: filterKey, option });
};

export const deselectSearchFilterOption = (
  filterKey: string,
  option: ?string
) => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  const options = state.mainSearch.filters[filterKey];
  invariant(Array.isArray(options), 'Invalid options type');
  const selectedOptions = options.filter(v => v !== option);
  trackEvent('Changed Search Filter', {
    filter_name: filterKey,
    filter_value: selectedOptions,
    ...getMainSearchTrackingData(state)
  });
  dispatch({ type: 'SEARCH_DESELECT_FILTER_OPTION', key: filterKey, option });
  dispatch(setQueryKeywords([]));
};

export const selectSearchFilterSpecificOptions = (
  filterKey: string,
  options: Array<string>
) => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  trackEvent('Changed Search Filter', {
    filter_name: filterKey,
    filter_value: options,
    ...getMainSearchTrackingData(state)
  });
  dispatch({
    type: 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS',
    key: filterKey,
    options
  });
  dispatch(setQueryKeywords([]));
};

export const selectAllSearchFilterOptions = (filterKey: string) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  trackEvent('Changed Search Filter', {
    filter_name: filterKey,
    filter_value: 'ALL',
    ...getMainSearchTrackingData(getState())
  });
  dispatch({ type: 'SEARCH_SELECT_ALL_FILTER_OPTIONS', key: filterKey });
  dispatch(setQueryKeywords([]));
};

export const setUserInitiatedSearch = (searchMode?: boolean = true) => ({
  type: 'SEARCH_INITIATED_BY_USER',
  searchMode
});

export const search = (
  { analytics }: { analytics: boolean } = { analytics: true }
) => (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: 'SEARCH_STARTED' });
  const state = getState();
  const { filters, searchId, resultsForSearchId } = state.mainSearch;
  const newSearch = searchId !== resultsForSearchId;

  if (newSearch && filters.type !== 'ALL' && filters.type !== 'ORG') {
    // Need to execute two searches to check if there are results if the user
    // selects the org filter instead
    return Promise.all([
      executeSearchQuery({ state, analytics }),
      fetchOrgResultsCount({ state })
    ]).then(([results, orgResultsCount]) => {
      dispatch(
        searchResultsSuccessful({
          results,
          hasOrgResults: orgResultsCount > 0
        })
      );
      return results;
    });
  }
  return executeSearchQuery({ state, analytics }).then(results => {
    if (newSearch) {
      const orgResultsCount =
        (results.facets && results.facets.type && results.facets.type.ORG) || 0;
      dispatch(
        searchResultsSuccessful({
          results,
          hasOrgResults: orgResultsCount > 0
        })
      );
    } else {
      dispatch(searchResultsSuccessful({ results }));
    }
    return results;
  });
};

export const fetchPage = (pageIndex: number) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  dispatch({ type: 'SEARCH_STARTED' });
  executeSearchQuery({ state: getState(), pageIndex, analytics: true }).then(
    results => {
      dispatch(fetchPageSuccessful({ results }));
    }
  );
};

export const clearResults = (): ClearResultsAction => ({
  type: 'SEARCH_CLEAR_RESULTS'
});

export const searchAutosaveDisable = () => (dispatch: Dispatch) => {
  dispatch({ type: 'SEARCH_AUTOSAVE_DISABLE' });
};
